import { useMemo } from "react";
import { Logger } from "@shared/utils/logger";
import { decodePassphrase } from "./client-utils";

interface E2EESetup {
  worker: Worker | undefined;
  e2eePassphrase: string | undefined;
  isE2EESupported: boolean;
}

const IS_BROWSER = typeof window !== "undefined";
const HAS_SHARED_ARRAY_BUFFER = IS_BROWSER && typeof SharedArrayBuffer !== "undefined";

/** Extracts the passphrase from the URL hash (e.g., `/room/abc#passphrase`). */
const extractPassphraseFromHash = (): string | undefined => {
  if (!IS_BROWSER) return undefined;
  const hash = window.location.hash.substring(1);
  return hash ? decodePassphrase(hash) : undefined;
};

/** Creates the E2EE worker if SharedArrayBuffer is available. */
const createE2EEWorker = (): Worker | undefined => {
  if (!HAS_SHARED_ARRAY_BUFFER) {
    Logger.warn("E2EE", "SharedArrayBuffer unavailable - check COOP/COEP headers");
    return undefined;
  }
  try {
    return new Worker(new URL("livekit-client/e2ee-worker", import.meta.url));
  } catch (error) {
    Logger.error("E2EE", "Worker creation failed:", error);
    return undefined;
  }
};

/**
 * Sets up E2EE for LiveKit.
 * Extracts the passphrase from the URL hash and initializes the E2EE worker.
 */
export const useSetupE2EE = (): E2EESetup => {
  const e2eePassphrase = useMemo(() => extractPassphraseFromHash(), []);

  const worker = useMemo((): Worker | undefined => {
    if (!e2eePassphrase) return undefined;
    return createE2EEWorker();
  }, [e2eePassphrase]);

  const isE2EESupported = useMemo(() => HAS_SHARED_ARRAY_BUFFER, []);

  return { worker, e2eePassphrase, isE2EESupported };
};
